// Library Methods
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  ResponsiveContainer,
  Customized,
  Text,
  ReferenceLine,
  ReferenceArea,
} from "recharts";

const RAPDChart = ({
  chartData,
  chartHeight = 400,
  testDuration = 20,
  isLiveUpdate = false,
}) => {
  const { t } = useTranslation();

  const hasNoEntries = useMemo(
    () => chartData?.pupilEventData?.length === 0,
    [chartData?.pupilEventData?.length]
  );

  // Convert a date string to UTC milliseconds
  const convertDateToMs = useCallback(
    (utcString) => DateTime.fromISO(utcString, { zone: "utc" }).toMillis(),
    []
  );

  // First event time in UTC milliseconds
  const startingTimeMs = useMemo(
    () => convertDateToMs(chartData?.pupilEventData?.[0]?.date),
    [chartData?.pupilEventData, convertDateToMs]
  );

  // Format all pupil events to have a startDate in milliseconds
  // (Measurement time starts at 0)
  const formattedPupilEvents = useMemo(
    () =>
      chartData?.pupilEventData?.map((pupilData) => ({
        ...pupilData,
        startDate: convertDateToMs(pupilData?.date) - startingTimeMs,
      })),
    [chartData?.pupilEventData, startingTimeMs, convertDateToMs]
  );

  // Format all DB update events to have a startDate in milliseconds
  // (Measurement time starts at 0)
  const formattedDBEvents = useMemo(
    () =>
      chartData?.fieldDBUpdatedEvents?.map((updatedEvent) => ({
        ...updatedEvent,
        startDate: convertDateToMs(updatedEvent?.startDate) - startingTimeMs,
      })),
    [chartData?.fieldDBUpdatedEvents, startingTimeMs, convertDateToMs]
  );

  // Format all blink events to have a startDate in milliseconds
  // (Measurement time starts at 0)
  const formattedBlinkEvents = useMemo(
    () =>
      chartData?.blinkEvents?.map((blinkEvent) => ({
        ...blinkEvent,
        startDate: convertDateToMs(blinkEvent?.startDate) - startingTimeMs,
        endDate: convertDateToMs(blinkEvent?.endDate) - startingTimeMs,
      })),
    [chartData?.blinkEvents, startingTimeMs, convertDateToMs]
  );

  // Formatted x axis ticks in milliseconds
  const chartTicks = useMemo(
    () =>
      Array.from({ length: Math.round(testDuration) + 1 }).map(
        (_, i) => i * 1000
      ),
    [testDuration]
  );

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
      }}
    >
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart
          width={500}
          height={200}
          data={formattedPupilEvents}
          syncId="anyId"
          margin={{
            top: 30,
            right: 15,
            left: hasNoEntries ? -45 : 5,
            bottom: hasNoEntries ? 0 : 30,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="startDate"
            textAnchor="end"
            angle="-40"
            type="number"
            scale="time"
            domain={["auto", "auto"]}
            interval={isLiveUpdate ? 3 : undefined}
            ticks={isLiveUpdate ? undefined : chartTicks}
            fontSize={12}
            tickFormatter={(value) => Math.round(value / 1000)} // display time in seconds
          >
            <Label value={`${t("Time")} (s)`} position="insideBottom" dy={30} />
          </XAxis>
          <YAxis
            fontSize={12}
            tickFormatter={(value) => value.toFixed(1)}
            allowDecimals={true}
            type="number"
          >
            <Label
              value={`${t("pupil_diameter")} (mm)`}
              position="insideLeft"
              angle={-90}
              style={{ textAnchor: "middle" }}
            />
          </YAxis>
          <Customized
            component={() => {
              return hasNoEntries ? (
                <Text
                  style={{
                    transform: `translate(50%, 50%)`,
                    textAnchor: "middle",
                    verticalAlign: "middle",
                  }}
                >
                  {t("no_data_text")}
                </Text>
              ) : null;
            }}
          />
          <Line
            type="monotone"
            dataKey="pupilDiameterLeft"
            stroke="red"
            fill="red"
            dot={<></>}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="pupilDiameterRight"
            stroke="blue"
            fill="blue"
            dot={<></>}
            isAnimationActive={false}
          />

          {formattedDBEvents?.map((updateEvent, index) => (
            <ReferenceLine
              key={`update-event-${index}`}
              x={updateEvent.startDate}
              stroke="black"
            >
              {updateEvent.updatedLeftDb !== null && (
                <Label
                  value={`${updateEvent.updatedLeftDb} dB`}
                  position="top"
                  fill="red"
                  fontSize={10}
                  dy={-13}
                />
              )}
              {updateEvent.updatedRightDb !== null && (
                <Label
                  value={`${updateEvent.updatedRightDb} dB`}
                  position="top"
                  fill="blue"
                  fontSize={10}
                  dy={0}
                />
              )}
            </ReferenceLine>
          ))}

          {formattedBlinkEvents?.map((blinkEvent, index) => (
            <ReferenceArea
              key={`blink-event-${index}`}
              x1={blinkEvent.startDate}
              x2={blinkEvent.endDate}
              stroke="green"
              fill="green"
              opacity={0.4}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RAPDChart;
